import * as React from "react";
import { get } from "lodash";
// Shared components
import Modal from "components/Modal";
import Button from "components/Button";
// UI Frameworks
import { Form, Typography, Button as AntButton, Spin, List, Row, Col, Alert, Radio } from "antd";
import { FormComponentProps } from "antd/lib/form";
// Shared components
import Maybe from "components/Maybe";
import InputNumber from "components/InputNumber";
// Hooks and utilities
import UserStore from "state/user";
import addCommas from "helpers/index";
import useWallet from "hooks/useWallet";
// Services
import { isFloat } from "helpers/index";
import { persianNumber } from "helpers/persian";
import BankAccountService, { AccountModel } from "services/http/endpoints/bank-accounts";
// Styles
import styles from "./index.module.scss";

const { Text } = Typography;

interface IProps extends FormComponentProps {
	unit: number;
	shouldVisible: boolean;
	onClose: () => void;
	diff: number;
	user: UserStore;
}

const InsufficientBalance: React.FC<IProps> = (props: IProps) => {
	const user = props.user ? props.user.getUser : {};
	const [rial, setRial] = React.useState(false);
	const { fundWallet } = useWallet();
	const [loading, setLoading] = React.useState(false);
	const [finnotech, setFinnotech] = React.useState(false);
	const [isFetching, setFetching] = React.useState(true);
	const [showAmountInfo, setShowAmountInfo] = React.useState(false);
	const [bankAccounts, setBankAccounts] = React.useState([] as AccountModel[]);
	const { getFieldDecorator, getFieldValue, setFieldsValue } = props.form;

	console.log(get(user, "usernameType") === "EMAIL");

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(() => {
		setFinnotech(props.user.baazarConfigs.ENABLE_FINNOTECH === "true");
		// get bank accounts
		if (isFetching && finnotech) {
			fetchData();
		}
	});

	const fetchData = async () => {
		try {
			setLoading(true);
			BankAccountService.makeOrderBy("createdDate", false);
			BankAccountService.makeFilters("type", "EQ", "CARD");
			BankAccountService.makeFilters("status", "EQ", "CONFIRMED");
			const result = await BankAccountService.search(BankAccountService.getSearchFilter());
			setLoading(false);
			setBankAccounts(result.result);
			setFetching(false);
		} catch (error) {
			setLoading(false);
		}
	};

	async function handleIncreaseBalanceSubmit() {
		try {
			setLoading(true);
			await fundWallet(getFieldValue("amount"));
			setLoading(false);
		} catch (error) {
			setLoading(false);
			throw error;
		}
	}

	/**
	 *
	 * @param e
	 */
	function handleSubmit(e: React.FormEvent): void {
		e.preventDefault();
		props.form.validateFields((err, values) => {
			if (!err && (props.user.baazarConfigs.ENABLE_FINNOTECH === "false" || bankAccounts.length > 0)) {
				setShowAmountInfo(false);
				handleIncreaseBalanceSubmit();
			}
		});
	}

	/**
	 * Validate amount
	 * @param callback
	 */
	function handleAmountValidation(_: any, value: number, callback: any) {
		if (!value) {
			callback("لطفا مقدار را وارد کنید.");
		} else if (isFloat(value)) {
			callback("لطفا مقدار اعشاری وارد نکنید.");
		} else if (value < 1000 || value > 10000000000) {
			callback(".مقدار وارد شده خارج از بازه (10,000,000,000 - 1,000) ریال می باشد");
		} else {
			callback();
		}
	}

	function handleUpdateAmount(value: number | undefined): void {
		const val = Number(persianNumber(value));

		if (val && val < 10000000000 && val > 1000) {
			setShowAmountInfo(true);
		} else {
			setShowAmountInfo(false);
		}
	}

	// function handleIncreaseDiffAmountSubmit() {
	// 	try {
	// 		setLoading(true);
	// 		dispatch({ current: 1, unit: props.affordableAssets });
	// 		setLoading(false);
	// 	} catch (error) {
	// 		setLoading(false);
	// 		throw error;
	// 	}
	// }

	return (
		<Modal title="شارژ حساب" visible={props.shouldVisible} onCancel={props.onClose} footer={null}>
			<Spin tip="در حال بارگزاری ..." spinning={loading} size="large">
				<Alert type="warning" message={`میزان شارژ حساب شما برای خرید ${props.unit} گرم طلا کافی نمی باشد`} />
				<Row type="flex" className={styles["increase-balance"]}>
					<div>
						برای خرید به میزان تعیین شده نیاز به افزایش موجودی کیف پول خود به مقدار{" "}
						<b>{addCommas(props.diff)}</b> ریال دارید.
					</div>
					<div style={{ margin: '20px -20px 20px 0' }}>
						{get(user, "usernameType") === "EMAIL" && (
							<Radio.Group
								onChange={e => setRial(e.target.value)}
								value={rial}
								className={styles["payment-type"]}
							>
								<Radio value={false}>پرداخت ارزی</Radio>
								<Radio value={true}>پرداخت ریالی</Radio>
							</Radio.Group>
						)}
					</div>
					<div style={{ margin: 'auto' }}>
						{!props.user.aanLogin && !rial && get(user, "usernameType") === "EMAIL" && (
							<div
								dangerouslySetInnerHTML={{
									__html:
										props.user.baazarConfigs.INTERNATIONAL_PARTNERS_LIST || "در حال بارگذاری...",
								}}
							/>
						)}
						{!props.user.aanLogin && (get(user, "usernameType") !== "EMAIL" || rial) && (
							<Maybe condition={!finnotech || bankAccounts.length > 0}>
								<Form onSubmit={handleSubmit} className={styles["increase-balance__form"]}>
									<Form.Item label="مقدار (ریال) :">
										{getFieldDecorator("amount", {
											initialValue: props.diff,
											rules: [
												{
													transform: value => persianNumber(value),
												},
												{
													validator: handleAmountValidation,
												},
											],
										})(
											<InputNumber
												className={styles["input-number"]}
												formatter={(value: any) =>
													persianNumber(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
												}
												parser={(value: any) => persianNumber(value).replace(/\$\s?|(,*)/g, "")}
												onChange={handleUpdateAmount}
											/>,
										)}
										{showAmountInfo && (
											<Alert
												message={
													<p>
														شما قصد پرداخت <b>{addCommas(getFieldValue("amount"))} ریال</b>{" "}
														را دارید.
													</p>
												}
												type="info"
											/>
										)}
									</Form.Item>
									{props.user.baazarConfigs.BUY_PRICES && (
										<Row gutter={[12, 12]}>
											{props.user.baazarConfigs.BUY_PRICES.split(",").map(
												(price: string, key: number) => (
													<Col key={key} md={8} xs={24}>
														<AntButton
															type="primary"
															onClick={() =>
																setFieldsValue({
																	amount: price,
																})
															}
															block
														>
															{addCommas(price)} ریال
														</AntButton>
													</Col>
												),
											)}
										</Row>
									)}
									{finnotech && (
										<List
											bordered
											style={{ marginTop: 10 }}
											header={<Text>برای پرداخت حتما از کارت های زیر استفاده کنید.</Text>}
											itemLayout="horizontal"
											dataSource={bankAccounts}
											renderItem={(item: any) => (
												<List.Item>
													<List.Item.Meta
														title={<Text>{item.name}</Text>}
														description={
															<span className="ltr">
																{item.value.match(/\d{4}/g).join("-")}
															</span>
														}
													/>
												</List.Item>
											)}
										/>
									)}
									<Form.Item className="button-center">
										<Button
											htmlType="submit"
											disabled={finnotech && bankAccounts.length === 0}
											info
										>
											تایید و پرداخت
										</Button>
									</Form.Item>
								</Form>
							</Maybe>
						)}
					</div>
					{/*props.affordableAssets !== 0 && (
						<Button
							className="auto-width"
							htmlType="submit"
							primary
							onClick={handleIncreaseDiffAmountSubmit}
						>
							خرید با حداکثر موجودی
						</Button>
					)*/}
				</Row>
			</Spin>
		</Modal>
	);
};

export default Form.create<IProps>({ name: "InsufficientBalance" })(InsufficientBalance);
